/**
 * MilestoneTypeKindCard — "This type is: our milestone / a feeder" on a milestone type's page in
 * Templates → Milestone (docs/OPERATING_MODEL.md, build step 2). A feeder is owed to us by someone
 * else and is never crewed; it names who owes it, and the coordinator who chases that kind of
 * feeder is shown (set firm-wide, one person per kind).
 */

import { useEffect, useState } from 'react'
import { listMilestoneTypeSettings, setMilestoneTypeKind, OWED_BY_LABELS, OWED_BY_KINDS } from '../services/milestoneTypes.js'

export default function MilestoneTypeKindCard({ typeId, addToast }) {
  const [data, setData] = useState(null)      // { settings, coordinators } once loaded
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    listMilestoneTypeSettings().then(d => { if (!cancelled) setData(d) }).catch(() => { if (!cancelled) setData({ settings: {}, coordinators: {}, error: true }) })
    return () => { cancelled = true }
  }, [typeId])

  if (!data) return null
  const s = data.settings[typeId] || { kind: 'milestone', owedBy: null }
  const coord = s.owedBy ? (data.coordinators || {})[s.owedBy] : null

  async function save(kind, owedBy) {
    setBusy(true)
    try {
      setData(await setMilestoneTypeKind(typeId, kind, owedBy))
      window.dispatchEvent(new CustomEvent('milestone-type-kind-changed', { detail: { typeId } }))
      addToast && addToast(kind === 'feeder' ? 'Saved as a feeder' : 'Saved as our milestone')
    } catch (e) {
      addToast && addToast(String(e && e.message || 'Save failed'), 'error')
    } finally { setBusy(false) }
  }

  const seg = (on) => ({
    fontFamily: 'DM Sans, sans-serif', fontSize: 12, padding: '4px 10px', cursor: busy ? 'default' : 'pointer',
    border: '0.5px solid var(--aj-border-mid)', background: on ? 'var(--aj-accent)' : 'var(--aj-bg-surface)',
    color: on ? '#fff' : 'var(--aj-text)',
  })

  return (
    <div style={{ margin: '10px 24px 0', padding: '10px 14px', borderRadius: 8, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', flexShrink: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 12.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)' }}>This type is</span>
        <div style={{ display: 'inline-flex' }}>
          <button disabled={busy} style={{ ...seg(s.kind !== 'feeder'), borderRadius: '4px 0 0 4px' }}
            onClick={() => s.kind === 'feeder' && save('milestone', null)}>Our milestone</button>
          <button disabled={busy} style={{ ...seg(s.kind === 'feeder'), borderRadius: '0 4px 4px 0', borderLeft: 'none' }}
            onClick={() => s.kind !== 'feeder' && save('feeder', 'agency')}>A feeder</button>
        </div>
        {s.kind === 'feeder' && (
          <>
            <span style={{ color: 'var(--aj-text-muted)' }}>owed by</span>
            <select disabled={busy} value={s.owedBy || 'agency'} onChange={e => save('feeder', e.target.value)}
              style={{ font: 'inherit', fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' }}>
              {OWED_BY_KINDS.map(k => <option key={k} value={k}>{OWED_BY_LABELS[k]}</option>)}
            </select>
            <span style={{ color: 'var(--aj-text-mid)' }}>
              {coord && coord.memberName ? `${coord.memberName} chases it (${coord.roleLabel})` : `${(coord && coord.roleLabel) || 'Coordinator'} not named yet`}
            </span>
          </>
        )}
      </div>
      {s.kind === 'feeder' && (() => {
        const t = (data.typical || {})[s.owedBy]
        return (
          <div style={{ marginTop: 6, color: 'var(--aj-text)', fontSize: 12.5 }}>
            {t
              ? <>Usually back in <b style={{ fontFamily: 'DM Mono, monospace' }}>{t.days} days</b> <span style={{ color: 'var(--aj-text-muted)' }}>· our typical time, from {t.basis} past returns. The schedule uses it until someone records a real expected date.</span></>
              : <span style={{ color: 'var(--aj-text-muted)' }}>No history yet for this kind of feeder, so the schedule keeps the length it was given until a real expected date is recorded.</span>}
          </div>
        )
      })()}
      <div style={{ marginTop: 6, color: 'var(--aj-text-muted)', fontSize: 11.5, lineHeight: 1.5 }}>
        {s.kind === 'feeder'
          ? 'Someone outside the firm does this work. It is never crewed and carries no kit; it waits on the Flight Board with who owes it and who chases it.'
          : 'Our own work, crewed on the Flight Board.'}
      </div>
    </div>
  )
}
